import { parseBankTransferDetails } from "@/lib/bank-transfer";
import type { HttpTypes } from "@medusajs/types";
import sdk from "@repo/api-sdk";
import { useMutation } from "@tanstack/react-query";

/**
 * The only two fields `initiatePaymentSession` reads off the cart it is given:
 * the id, and whether a payment collection already exists for it. Named here so
 * the retrieve below can ask for exactly that and nothing else.
 */
const PAYMENT_SESSION_CART_FIELDS = "id,payment_collection.id";

/** A payment session as the initiate response carries it. */
type InitiatedPaymentSession = NonNullable<
  HttpTypes.StorePaymentCollection["payment_sessions"]
>[number];

/**
 * The session to run this cart's payment against: the last one the response
 * lists for `providerId`, or undefined if it lists none.
 *
 * Overlapping initiations can leave a collection holding more than one session
 * for a provider. The last is preferred as the likeliest to be the one this
 * call just created, but nothing rests on that - the caller reads the id *and*
 * the client secret off whichever object this returns, so the pair is
 * self-consistent either way, and a wrong pick fails safe (the backend refuses
 * and the step re-initialises). The response's ordering is not specified, so
 * "last" is a guess: don't build anything on it that needs it to be right.
 */
function sessionFor(
  sessions: InitiatedPaymentSession[] | undefined,
  providerId: string
): InitiatedPaymentSession | undefined {
  const forProvider = (sessions ?? []).filter(
    (s) => s.provider_id === providerId
  );
  return forProvider[forProvider.length - 1];
}

/**
 * Initiates a payment session for a single cart with the chosen provider.
 *
 * 1. Reads the cart's id and payment-collection id
 * 2. Calls sdk.store.payment.initiatePaymentSession with the selected provider
 * 3. Returns the provider-specific details the checkout UI needs off the new
 *    session's `data`: `clientSecret` for Stripe-based providers, `bankTransfer`
 *    for the bank-transfer provider. Each is undefined/null for the others.
 */
export function useInitiatePaymentSession() {
  return useMutation({
    mutationFn: async ({
      cartId,
      providerId
    }: {
      cartId: string;
      providerId: string;
    }) => {
      // This read used to pull the cart's full field graph - Medusa's ~120-field
      // store-cart default - to hand the SDK an object it reads exactly two
      // properties from, and it sat on the critical path to enabling "Pay now".
      //
      // It is narrowed rather than deleted, because the SDK's
      // `payment_collection?.id` check is load-bearing: when it finds none it
      // POSTs /store/payment-collections, and core's
      // createPaymentCollectionForCartWorkflow *throws* on a cart that already
      // has one. So the answer has to be current, which a cart read on page
      // mount (or a cached list) cannot guarantee once the buyer has already
      // initiated once and switched provider. One cheap round trip buys that.
      //
      // The `+payment_collection.payment_sessions` this used to request was a
      // no-op: both are in Medusa's store-cart defaults already.
      const { cart } = await sdk.store.sellerCart.retrieve(cartId, {
        fields: PAYMENT_SESSION_CART_FIELDS
      });

      // Initiate payment session — creates payment collection if needed
      const result = await sdk.store.payment.initiatePaymentSession(cart, {
        provider_id: providerId
      });

      // Prefer the session belonging to the provider we just initiated. A
      // collection can hold sessions for more than one provider, so index 0 is
      // not reliably ours: reading it would hand the bank-transfer panel a
      // Stripe session's data, and vice versa.
      //
      // No fallback to another provider's session: a miss here means the
      // response does not yet carry the provider we asked for, and returning
      // nothing beats returning a payload the caller would misread.
      const session = sessionFor(
        result.payment_collection.payment_sessions,
        providerId
      );
      const data = session?.data;

      const clientSecret = data?.client_secret as string | undefined;
      const bankTransfer = parseBankTransferDetails(data);

      // Id and secret come off the *same* session object on purpose - two
      // lookups could disagree about which session was paid, one cannot, and
      // completion is settled against the id. `CONTEXT.md` ->
      // "Provider-session pairing".
      return {
        cartId,
        paymentSessionId: session?.id,
        clientSecret,
        bankTransfer
      };
    }
    // Deliberately no cart invalidation. Initiating a payment session attaches
    // a payment collection to the cart; it changes no field the checkout page
    // renders - not the items, totals, addresses or shipping methods. The
    // invalidation that used to live here cost two `staleTime: 0` refetches per
    // cart (the list and the detail), and the refetched list handed
    // `CheckoutPage` a new `carts` array identity, which re-rendered
    // `PaymentStep` in the middle of initialising its sessions.
  });
}

/**
 * The payment session ids still on record for these carts, read fresh off the
 * server rather than trusted from the in-memory checkout plan.
 *
 * `CheckoutOps.currentPaymentSessionIds` (`pay-for-carts.ts`) is why this
 * exists: applying an address can change a cart's total, and Medusa deletes a
 * payment collection's sessions outright the moment its amount stops
 * agreeing with the cart's - so a session confirmed against a moment ago can
 * simply not be here any more (KAN-134). A plain async function, not a
 * mutation hook, because it is a one-off read `payForCarts` awaits directly,
 * not something a component's render triggers.
 */
export async function currentPaymentSessionIds(
  cartIds: string[]
): Promise<Set<string>> {
  const retrieved = await Promise.all(
    cartIds.map((cartId) =>
      sdk.store.sellerCart.retrieve(cartId, {
        fields: "id,payment_collection.payment_sessions.id"
      })
    )
  );
  return new Set(
    retrieved.flatMap(
      ({ cart }) =>
        cart.payment_collection?.payment_sessions?.map((s) => s.id) ?? []
    )
  );
}

/**
 * Completes a seller cart after payment confirmation.
 * Creates the order from the cart.
 *
 * `paymentSessionId` names the session this cart's payment was confirmed
 * against; the server settles that one or refuses. See `CONTEXT.md` ->
 * "Provider-session pairing".
 *
 * NOTE: No onSuccess invalidation here — completed carts return 400 on refetch.
 * The caller (PaymentStep) handles cache cleanup via removeQueries after all
 * completions finish, before navigating away.
 */
export function useCompletePayment() {
  return useMutation({
    mutationFn: ({
      cartId,
      paymentSessionId,
      orderSetId
    }: {
      cartId: string;
      paymentSessionId?: string;
      orderSetId?: string;
    }) =>
      sdk.store.sellerCart.complete(cartId, {
        ...(orderSetId ? { order_set_id: orderSetId } : {}),
        ...(paymentSessionId ? { payment_session_id: paymentSessionId } : {})
      })
  });
}
